'use client'

import Link from 'next/link'
import { useTranslation } from '@/i18n/client'
import {
  AwardLegalShell,
  AwardDataCategory,
  type LegalArticle,
} from '@/components/award/legal-shell'

const articles: LegalArticle[] = [
  {
    num: '§ 1', numDisplay: '01', title: 'Verantwortlicher (Art. 4 Nr. 7 DSGVO)', titleI18nKey: 'legal.datenschutzTitles.t01', summaryI18nKey: 'legal.datenschutzSummaries.t01',
    body: (
      <>
        <p>
          Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) und des
          Bundesdatenschutzgesetzes (BDSG) ist:
        </p>
        <div className="award-contact-card" style={{ marginTop: '1.2em' }}>
          <div className="award-contact-label">Verantwortlicher</div>
          <div className="award-contact-value">
            <strong>Stephan Eich, Modellpilot.EU</strong>
            <br />
            Sedanstrasse 2 · 12167 Berlin · Deutschland
            <br />
            Telefon: <a href="tel:+493039885788">+49 30 39885788</a>
            <br />
            E-Mail: <a href="mailto:info@free-glide-league.com">info@free-glide-league.com</a>
          </div>
        </div>
      </>
    ),
  },
  {
    num: '§ 2', numDisplay: '02', title: 'Datenschutzbeauftragter', titleI18nKey: 'legal.datenschutzTitles.t02', summaryI18nKey: 'legal.datenschutzSummaries.t02',
    body: (
      <p>
        Ein Datenschutzbeauftragter ist gesetzlich nicht erforderlich (§ 38 BDSG). Bei
        Fragen zum Datenschutz wenden Sie sich direkt an den Verantwortlichen unter{' '}
        <a href="mailto:info@free-glide-league.com">info@free-glide-league.com</a>.
      </p>
    ),
  },
  {
    num: '§ 3', numDisplay: '03', title: 'Grundsätze der Datenverarbeitung', titleI18nKey: 'legal.datenschutzTitles.t03', summaryI18nKey: 'legal.datenschutzSummaries.t03',
    body: (
      <>
        <p style={{
          fontSize: '1.05em',
          fontWeight: 600,
          padding: '0.85rem 1rem',
          borderLeft: '3px solid var(--award-gold)',
          background: 'color-mix(in oklch, var(--award-gold) 8%, transparent)',
          marginBottom: '1rem',
        }}>
          Die Plattform FREE GLIDE LEAGUE ist eine Analyse- und Wertungs-Plattform
          für RC-Modellsegelflug.
        </p>
        <p>
          Es werden{' '}
          <strong>ausschließlich Daten verarbeitet, die durch den Nutzer selbst aktiv
          hochgeladen oder eingegeben werden</strong>. Es findet kein Import aus
          Drittquellen, kein Scraping und keine automatische Übernahme fremder Daten statt.
        </p>
        <p>
          Die Verarbeitung erfolgt nach den Grundsätzen der Rechtmäßigkeit, der
          Datensparsamkeit, der Zweckbindung und der Transparenz (Art. 5 DSGVO).
        </p>
      </>
    ),
  },
  {
    // R102.6 (Stephan-Bug 2026-05-31): § 4 mit 12 Unterpunkten war so lang dass
    // die Seite bei mehreren Browsern abbrach / der ganze Paragraph nicht mehr
    // rendert wurde. Aufgeteilt in 4a/4b/4c nach logischer Gruppierung:
    //   4a — Konto + Identität (Registrierung, Geburtsdatum, Einwilligungs-Audit)
    //   4b — Flug- + Wettbewerbsdaten (IGC, Atlas, Wetter, KI-Analyse)
    //   4c — Service + freiwillige Funktionen (Logs, Newsletter, Push, Waiver, Donator, Bug-Reports)
    num: '§ 4a', numDisplay: '04a', title: 'Konto- und Identitätsdaten', titleI18nKey: 'legal.datenschutzTitles.t04a', summaryI18nKey: 'legal.datenschutzSummaries.t04a',
    body: (
      <>
        <AwardDataCategory num="(1)" title="Registrierung und Nutzerkonto">
          <p><strong>Daten:</strong> E-Mail-Adresse, Passwort (bcrypt-Hash), Pilotenname,
            optional Vereinszugehörigkeit, Sprache, Zeitzone, Profilbild.</p>
          <p><strong>Zweck:</strong> Bereitstellung eines persönlichen Nutzerkontos,
            Authentifizierung, Kommunikation.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).</p>
        </AwardDataCategory>

        <AwardDataCategory num="(1a)" title="Geburtsdatum — Altersprüfung (Art. 8 DSGVO)">
          <p><strong>Daten:</strong> Tag-genaues Geburtsdatum. Bei Piloten zwischen 14 und 16 Jahren
            zusätzlich die E-Mail-Adresse eines Erziehungsberechtigten sowie der Zeitpunkt und
            die IP-Adresse der Elternbestätigung.</p>
          <p><strong>Zweck:</strong> Prüfung des Mindestalters (Registrierung erst ab 14 Jahren),
            Einholung der Einwilligung eines Erziehungsberechtigten bei 14- bis 15-Jährigen
            (Double-Opt-In). Nachweis der Altersprüfung nach Art. 5 Abs. 2 DSGVO.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. c DSGVO i.V.m. Art. 8 DSGVO
            (gesetzliche Verpflichtung zur Altersprüfung) sowie bei Minderjährigen Art. 8 Abs. 2
            DSGVO.</p>
          <p><strong>Speicherdauer:</strong> Solange das Konto besteht. Das Jahr wird ausschließlich
            intern zur Altersprüfung gespeichert und niemals öffentlich angezeigt.</p>
        </AwardDataCategory>

        <AwardDataCategory num="(1b)" title="Geburtsdatum — Geburtstagschat (optional)">
          <p><strong>Daten:</strong> Monat und Tag des Geburtsdatums (ohne Jahr). Opt-In-Status
            und Zeitpunkt der Einwilligung werden zu Nachweiszwecken gespeichert.</p>
          <p><strong>Zweck:</strong> Anzeige im Community-Geburtstagschat am Geburtstag des
            Nutzers. Nur Monat und Tag sichtbar — Jahr bleibt jederzeit privat.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (freiwillige Einwilligung,
            jederzeit widerrufbar nach Art. 7 Abs. 3 DSGVO).</p>
        </AwardDataCategory>

        <AwardDataCategory num="(2)" title="Einwilligungs-Nachweis (Audit-Log)">
          <p><strong>Daten:</strong> Zeitstempel der Annahme, IP-Adresse, User-Agent,
            Versionsnummer und SHA-256-Hash der akzeptierten Dokumente.</p>
          <p><strong>Zweck:</strong> Rechenschaftspflicht nach Art. 7 Abs. 1 und Art. 5 Abs. 2
            DSGVO.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. c DSGVO (gesetzliche
            Verpflichtung).</p>
        </AwardDataCategory>
      </>
    ),
  },
  {
    num: '§ 4b', numDisplay: '04b', title: 'Flug- und Wettbewerbsdaten', titleI18nKey: 'legal.datenschutzTitles.t04b', summaryI18nKey: 'legal.datenschutzSummaries.t04b',
    body: (
      <>
        <AwardDataCategory num="(1)" title="Flugdaten-Upload (IGC-Dateien) & Wertungs-Veröffentlichung">
          <p><strong>Daten:</strong> IGC-Dateien mit GPS-Track, Höhen- und Zeitinformationen,
            Start- und Landekoordinaten, optional Logger-Typ und Seriennummer, Modell- und
            Klassenzuordnung. Veröffentlichte Wertungsdaten: Pilotinnen-/Pilotenname (oder
            Pilotenpseudonym), Region/Fluggebiet, Modellangaben, Distanz, Flugdauer,
            Wertungspunkte, Klassenzuordnung, Datum.</p>
          <p><strong>Zweck:</strong> Analyse des Fluges, Berechnung von Polaren, Prüfung gegen
            Wettbewerbsregeln, Darstellung in Ranglisten, Visualisierung.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
            und Art. 6 Abs. 1 lit. a DSGVO (Einwilligung für Veröffentlichung in Ranglisten).</p>

          <p style={{ marginTop: '1rem', padding: '0.85rem 1rem', borderLeft: '3px solid var(--award-gold)', background: 'color-mix(in oklch, var(--award-gold) 8%, transparent)' }}>
            <strong style={{ color: 'var(--award-gold)' }}>Hinweis auf Bewegungsdaten:</strong>{' '}
            Mit der Einreichung eines Fluges willigt die Pilotin / der Pilot ausdrücklich in die
            öffentliche Anzeige der Wertungsdaten ein und erkennt zugleich an, dass aus den im
            Zusammenhang mit Flügen veröffentlichten Daten — insbesondere Datum, Uhrzeit, Flugort,
            Flugdauer und Häufigkeit von Flügen — unter Umständen <strong>Rückschlüsse auf
            Aufenthaltsorte und Bewegungsmuster</strong> gezogen oder interpretiert werden können.
            Die Pilotin / der Pilot stimmt dieser Möglichkeit ausdrücklich zu. Die Einwilligung
            kann jederzeit mit Wirkung für die Zukunft widerrufen werden (Art. 7 Abs. 3 DSGVO);
            die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt.
          </p>
          <p style={{ fontSize: '0.92em', color: 'var(--award-cirrus-3)' }}>
            Die Einwilligung wird beim Onboarding über eine separate, nicht voraktivierte Pflicht-Checkbox
            eingeholt und ist Voraussetzung für die Teilnahme an der Wertung. Wer die Einwilligung
            nicht erteilt oder später widerruft, kann die Plattform weiterhin nutzen — die
            Wertungs-Veröffentlichung entfällt dann jedoch.
          </p>
        </AwardDataCategory>

        <AwardDataCategory num="(2)" title="Thermik-Atlas (anonymisierte Aggregatdaten)">
          <p><strong>Daten:</strong> Aggregierte Hotspot-Zellen aus den Flugtracks aktiver
            Nutzer — ohne Personenbezug, ohne Einzeltracks, ohne Zeitstempel.</p>
          <p><strong>Zweck:</strong> Community-Nutzen (Thermik-Karte für alle Piloten).</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes
            Interesse — Community-Mehrwert). Pro Flug opt-out möglich.</p>
        </AwardDataCategory>

        <AwardDataCategory num="(3)" title="Wetterdaten-Verknüpfung">
          <p><strong>Daten:</strong> Start-Koordinaten des Fluges (Breiten- und Längengrad,
            gerundet auf max. 4 Nachkommastellen) werden je nach Region an unterschiedliche
            Wetterdienste übertragen, um das Wetter zum Flugzeitpunkt abzurufen:</p>
          <ul>
            <li><strong>Bright Sky / DWD Open Data</strong> (Deutschland, Österreich, Schweiz,
              Liechtenstein) — präzise aktuelle Bedingungen aus DWD-Stationsdaten.
              Lizenz GeoNutzV 2017.</li>
            <li><strong>NOAA / National Weather Service</strong> (USA — CONUS, Alaska, Hawaii)
              über <code>api.weather.gov</code>. NWS-Daten sind als Werke der US-Bundesregierung
              nach 17 U.S.C. § 105 gemeinfrei (kostenlos, kommerziell erlaubt). Es werden
              ausschließlich Koordinaten und ein identifizierender User-Agent der FGL-Plattform
              übertragen, keine personenbezogenen Pilot-Daten. NOAA-Server stehen in den USA;
              da nur die Hetzner-Server-IP der FGL-Plattform und keine Pilot-IP übermittelt
              wird, liegt keine personenbezogene Drittland-Übermittlung vor.</li>
            <li><strong>JMA / Japan Meteorological Agency (気象庁)</strong> (Japan) über
              <code>www.jma.go.jp/bosai</code>. Lizenz „Government of Japan Standard Terms
              of Use 2.0" (kostenlos, kommerziell erlaubt mit Quellenangabe). Übermittelt
              wird ausschließlich der JMA-Area-Code der nächstgelegenen Präfektur (z.&nbsp;B.
              <code>130000</code> für Tokyo) — keine Pilot-Koordinaten werden weitergegeben.
              Für Japan besteht ein EU-Angemessenheitsbeschluss vom 23.01.2019 nach Art. 45 DSGVO.</li>
            <li><strong>MET Norway / Yr.no</strong> (Norwegisches Meteorologisches Institut,
              Oslo, Norwegen) — weltweite Vorhersage über die API <code>api.met.no</code>,
              Lizenz NLOD 2.0 (kostenlos, kommerziell erlaubt). Es werden ausschließlich
              Koordinaten und der identifizierende User-Agent der FGL-Plattform übertragen,
              keine personenbezogenen Pilot-Daten. Norwegen verfügt über einen
              EWR-Angemessenheitsbeschluss; eine Drittland-Übermittlung im Sinne der DSGVO
              liegt nicht vor.</li>
          </ul>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).</p>
        </AwardDataCategory>

        <AwardDataCategory num="(4)" title="KI-Analyse">
          <p><strong>Daten:</strong> Anonymisierte Flugkennzahlen (Distanz, Dauer, Klasse,
            Thermik-Anzahl, Windwerte) werden zur Generierung eines Coaching-Textes an einen
            eigenen KI-Hochleistungsserver in Deutschland übertragen.</p>
          <p><strong>Besonderheit:</strong> Die KI läuft auf eigener Infrastruktur in
            Deutschland und fällt auf einen Fallback-Server bei Hetzner (Deutschland)
            zurück. Es werden <strong>keine Daten an externe KI-Anbieter</strong>{' '}
            (z.B. OpenAI, Anthropic, Google Gemini) übertragen.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung — opt-in).</p>
        </AwardDataCategory>
      </>
    ),
  },
  {
    num: '§ 4c', numDisplay: '04c', title: 'Service- und freiwillige Daten', titleI18nKey: 'legal.datenschutzTitles.t04c', summaryI18nKey: 'legal.datenschutzSummaries.t04c',
    body: (
      <>
        <AwardDataCategory num="(1)" title="Server-Logs">
          <p><strong>Daten:</strong> IP-Adresse (gekürzt nach 7 Tagen), Zeitstempel, aufgerufene
            URL, Referrer, User-Agent, HTTP-Status, Datenmenge.</p>
          <p><strong>Zweck:</strong> Abwehr von Angriffen, Fehleranalyse, Stabilität.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes
            Interesse an IT-Sicherheit). <strong>Speicherdauer:</strong> Maximal 7 Tage.</p>
        </AwardDataCategory>

        <AwardDataCategory num="(2)" title="Newsletter (optional)">
          <p><strong>Daten:</strong> E-Mail-Adresse, Sprache, Frequenz-Einstellung, Inhalts-Toggles.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung,
            Double-Opt-In). Abmeldung jederzeit über HMAC-Token-Link in jeder E-Mail.</p>
        </AwardDataCategory>

        <AwardDataCategory num="(3)" title="Push-Benachrichtigungen (optional)">
          <p><strong>Daten:</strong> Web-Push-Subscription (Endpoint-URL + Public Keys).</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).</p>
        </AwardDataCategory>

        <AwardDataCategory num="(4)" title="Haftungsausschluss-Nachweis (Waiver)">
          <p>Bei sicherheitsrelevanten Aktionen wird ein versionierter Haftungsausschluss
            akzeptiert und protokolliert: Datum/Uhrzeit (UTC), IP/User-Agent, SHA-256-Hash
            des Volltextes, Versionsnummer, Verknüpfung zur Aktion.</p>
          <p>Bei Aktualisierung des Dokuments wird der Nutzer um Neuakzeptanz gebeten. Erfolgt
            diese nicht innerhalb von zwei Erinnerungen (14 + 7 Tage), wird das Konto deaktiviert.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO und Art. 7 DSGVO.</p>
        </AwardDataCategory>

        <AwardDataCategory num="(5)" title="Donator-Status (optional)">
          <p><strong>Daten:</strong> Kennzeichnung als Unterstützer („Donator"), Datum des
            ersten Digitalen Trinkgeldes bzw. Förderbeitrags (<code>donator_since</code>) und
            Ablaufdatum des aktuellen Ringes (<code>donator_expires_at</code>, jeweils 365 Tage
            ab dem letzten Trinkgeld bzw. Förderbeitrag, RESET-Logik).</p>
          <p><strong>Zweck:</strong> Darstellung eines farbigen Ringes um das Profilbild
            (5 verschiedene Ringe), befristet auf 365 Tage pro Digitalem Trinkgeld bzw.
            Förderbeitrag. Bei Monats-Förderbeiträgen läuft der Ring durchgehend solange
            der Beitrag aktiv ist.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) und
            Art. 6 Abs. 1 lit. f DSGVO (transparente Unterstützer-Anerkennung).</p>
        </AwardDataCategory>

        <AwardDataCategory num="(6)" title="Bug-Reports">
          <p><strong>Daten:</strong> Titel, Beschreibung, Screenshots, URL, User-Agent,
            Viewport — freiwillig eingereicht.</p>
          <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (Qualitätssicherung).</p>
        </AwardDataCategory>
      </>
    ),
  },
  {
    num: '§ 5', numDisplay: '05', title: 'Empfänger und Auftragsverarbeiter', titleI18nKey: 'legal.datenschutzTitles.t05', summaryI18nKey: 'legal.datenschutzSummaries.t05',
    body: (
      <>
        <p>
          Zur Bereitstellung der Plattform werden folgende Auftragsverarbeiter im Sinne
          des Art. 28 DSGVO eingesetzt. Mit den genannten Anbietern bestehen die jeweils
          erforderlichen Auftragsverarbeitungsverträge (AVV) bzw. Data Processing
          Addenda (DPA):
        </p>
        <ul>
          <li><strong>Hetzner Online GmbH</strong>, Industriestr. 25, 91710 Gunzenhausen,
            Deutschland — Hosting (Server, Datenbank, Objektspeicher, Backup-Storage-Box)
            für sämtliche Plattform-Daten am Standort Falkenstein/Vogtland. Keine
            Drittland-Übermittlung.{' '}
            <strong>AVV unterzeichnet am 28.04.2026</strong> (Vertragsversion 1.2 vom
            16.02.2026; TÜV-Rheinland-Bericht 63017991-01 vom 19.02.2026, ISO 27001
            bis 09/2028, BSI C5 Typ 2 seit 12/2025).</li>
          <li><strong>Mittwald CM Service GmbH &amp; Co. KG</strong>, Königsberger Straße 4–6,
            32339 Espelkamp, Deutschland — SMTP-Versand transaktionaler E-Mails
            (Registrierungs-Bestätigung, Passwort-Reset, Konto-Benachrichtigungen) sowie
            Newsletter-Versand. Verarbeitet werden Empfänger-E-Mail, IP-Adresse des
            sendenden FGL-Servers und der Mail-Body. Keine Drittland-Übermittlung.{' '}
            <strong>AVV-Status:</strong> Auftragsverarbeitungsvertrag mit Mittwald wird
            vor dem öffentlichen Launch (03.06.2026) im Mittwald-Kundencenter formal
            abgeschlossen.</li>
          <li><strong>Cloudflare, Inc.</strong>, 101 Townsend St, San Francisco, CA 94107,
            USA (Cloudflare Turnstile CAPTCHA) — Bot-Abwehr bei Registrierung und
            sicherheitsrelevanten Formularen. Übermittelt werden ein Turnstile-Cookie,
            IP-Adresse und User-Agent zur Sitzung. Drittland-Transfer auf Grundlage von
            Standardvertragsklauseln (SCC) nach Art. 46 Abs. 2 lit. c DSGVO; ergänzend
            DPA von Cloudflare. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO.</li>
          <li><strong>Tailscale Inc.</strong>, 119 Spadina Ave, Toronto, ON M5V 2L1, Kanada
            — Bereitstellung des verschlüsselten WireGuard-Mesh-Tunnels zwischen der
            FGL-Plattform (Hetzner) und dem eigenen KI-Hochleistungsserver in
            Deutschland. Tailscale verarbeitet ausschließlich Verbindungs-Metadaten
            (Geräte-IDs, öffentliche WireGuard-Keys, Verbindungsstatus) — <strong>keine
            Inhalts-, Prompt- oder Antwortdaten</strong>, da der Tunnel Ende-zu-Ende
            verschlüsselt zwischen den FGL-Knoten läuft. Für Kanada besteht ein
            EU-Angemessenheitsbeschluss der Kommission vom 20.12.2001 (PIPEDA);
            ergänzend stellt Tailscale ein DPA bereit. Rechtsgrundlage: Art. 6 Abs. 1
            lit. f DSGVO i.&nbsp;V.&nbsp;m. Art. 45 DSGVO.</li>
          <li><strong>PayPal (Europe) S.à r.l. et Cie, S.C.A.</strong>, 22-24 Boulevard
            Royal, L-2449 Luxembourg — Zahlungsabwicklung bei freiwilligen
            Digitalen Trinkgeldern und Förderbeiträgen.
            PayPal verarbeitet die Daten in eigener Verantwortung als Verantwortlicher
            i.&nbsp;S.&nbsp;d. Art. 4 Nr. 7 DSGVO.{' '}
            <a href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full" target="_blank" rel="noopener noreferrer">
              PayPal-Datenschutz
            </a>.</li>
        </ul>
        <p style={{ marginTop: '1.2em' }}>
          Daneben werden offene Datendienste eingebunden, die <strong>keine
          personenbezogenen Pilot-Daten</strong> übertragen, sondern ausschließlich
          Koordinaten oder Area-Codes vom FGL-Server abrufen:
        </p>
        <ul>
          <li><strong>Deutscher Wetterdienst (DWD)</strong>, Offenbach am Main — offene
            Wetterdaten nach GeoNutzV 2017 für die Region DACH (über Bright Sky API).</li>
          <li><strong>NOAA / National Weather Service</strong>, Silver Spring, Maryland, USA —
            Wetterdaten für USA (CONUS, Alaska, Hawaii) über{' '}
            <code>api.weather.gov</code>. NWS-Daten sind als US-Government-Werke nach
            17 U.S.C. § 105 gemeinfrei. Es werden ausschließlich Koordinaten und ein
            identifizierender User-Agent der FGL-Server-IP übertragen, keine
            Pilot-IPs und keine personenbezogenen Daten.</li>
          <li><strong>JMA / Japan Meteorological Agency (気象庁)</strong>, Tokyo, Japan
            (Angemessenheitsbeschluss der EU-Kommission vom 23.01.2019 nach Art. 45
            DSGVO) — Wetterdaten für Japan über <code>www.jma.go.jp/bosai</code> unter
            „Government of Japan Standard Terms of Use 2.0". Übermittelt wird
            ausschließlich der Area-Code der nächstgelegenen Präfektur.</li>
          <li><strong>MET Norway / Norwegisches Meteorologisches Institut</strong>, Oslo,
            Norwegen (EWR) — weltweite Wettervorhersage über <code>api.met.no</code> nach
            NLOD 2.0. Es werden ausschließlich Koordinaten und ein identifizierender
            User-Agent der FGL-Server-IP übertragen.</li>
          <li><strong>openAIP</strong> — Luftraum-Daten (community-gepflegt) über
            anonymen Server-Proxy. Keine personenbezogenen Daten werden übertragen.</li>
          <li><strong>MinIO / S3-Storage</strong> (auf Hetzner-Servern in Deutschland) —
            Speicherung von IGC-Dateien, Bildern und Medien. Keine Drittland-Übermittlung.</li>
        </ul>
      </>
    ),
  },
  {
    num: '§ 6', numDisplay: '06', title: 'Speicherdauer', titleI18nKey: 'legal.datenschutzTitles.t06', summaryI18nKey: 'legal.datenschutzSummaries.t06',
    body: (
      <ul>
        <li><strong>Konto-Daten:</strong> für die Dauer der Mitgliedschaft; bei Kündigung
          sofortige Löschung oder Anonymisierung nach Art. 17 DSGVO.</li>
        <li><strong>Flugdaten:</strong> bis zum Widerruf oder zur Konto-Löschung.</li>
        <li><strong>Audit-Logs (Einwilligungs-Nachweise):</strong> bis 3 Jahre nach Ende des
          Vertragsverhältnisses (Art. 7 DSGVO, Verjährungsfristen).</li>
        <li><strong>Server-Logs:</strong> maximal 7 Tage.</li>
        <li><strong>Newsletter-Einwilligung:</strong> bis zum Widerruf.</li>
        <li><strong>Thermik-Atlas (aggregiert):</strong> unbegrenzt — enthält keinen
          Personenbezug mehr.</li>
      </ul>
    ),
  },
  {
    num: '§ 7', numDisplay: '07', title: 'Ihre Rechte als Nutzer (Rechte der Betroffenen)', titleI18nKey: 'legal.datenschutzTitles.t07', summaryI18nKey: 'legal.datenschutzSummaries.t07',
    body: (
      <>
        <p>Sie haben jederzeit folgende Rechte gegenüber dem Verantwortlichen:</p>
        <ul>
          <li><strong>Auskunft</strong> (Art. 15 DSGVO) — welche Daten über Sie gespeichert sind.</li>
          <li><strong>Berichtigung</strong> (Art. 16 DSGVO) — Korrektur falscher Daten.</li>
          <li><strong>Löschung</strong> (Art. 17 DSGVO) — „Recht auf Vergessenwerden".</li>
          <li><strong>Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO).</li>
          <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO) — Export Ihrer Daten im
            maschinenlesbaren Format (ZIP/JSON).</li>
          <li><strong>Widerspruch</strong> (Art. 21 DSGVO) — insbesondere gegen Verarbeitungen
            auf Grundlage berechtigter Interessen.</li>
          <li><strong>Widerruf einer Einwilligung</strong> (Art. 7 Abs. 3 DSGVO).</li>
          <li><strong>Keine automatisierten Einzelentscheidungen</strong> (Art. 22 DSGVO) —
            die Wertungs-Plausibilitätsprüfung wird vom Veranstalter manuell bestätigt.</li>
        </ul>
        <p>
          Einige dieser Rechte können direkt im Konto ausgeübt werden (Datenexport, Löschung
          über die Kontoeinstellungen). Alternativ per E-Mail an{' '}
          <a href="mailto:info@free-glide-league.com">info@free-glide-league.com</a>.
        </p>
      </>
    ),
  },
  {
    num: '§ 8', numDisplay: '08', title: 'Beschwerderecht bei Aufsichtsbehörde', titleI18nKey: 'legal.datenschutzTitles.t08', summaryI18nKey: 'legal.datenschutzSummaries.t08',
    body: (
      <>
        <p>
          Sie haben nach Art. 77 DSGVO das Recht, sich bei einer Datenschutz-Aufsichtsbehörde
          zu beschweren. Zuständig für den Betreiber ist:
        </p>
        <div className="award-contact-card" style={{ marginTop: '1.2em' }}>
          <div className="award-contact-label">Aufsichtsbehörde</div>
          <div className="award-contact-value">
            <strong>Berliner Beauftragte für Datenschutz und Informationsfreiheit</strong>
            <br />
            Friedrichstr. 219 · 10969 Berlin
            <br />
            +49 30 13889-0 · <a href="mailto:mailbox@datenschutz-berlin.de">mailbox@datenschutz-berlin.de</a>
            <br />
            <a href="https://www.datenschutz-berlin.de" target="_blank" rel="noopener noreferrer">datenschutz-berlin.de</a>
          </div>
        </div>
      </>
    ),
  },
  {
    num: '§ 8a', numDisplay: '8a', title: 'Internationale Nutzer und zusätzliche Beschwerdestellen', titleI18nKey: 'legal.datenschutzTitles.t8a', summaryI18nKey: 'legal.datenschutzSummaries.t8a',
    body: (
      <>
        <p>
          FGL ist eine in Deutschland betriebene Plattform, die weltweit allen Piloten
          offensteht. Für die Verarbeitung personenbezogener Daten gilt{' '}
          <strong>primär die DSGVO</strong> sowie ergänzend deutsches Datenschutzrecht (BDSG,
          TTDSG, DDG). Nutzer aus Ländern außerhalb der EU/EWR haben nach ihrem jeweiligen
          Heimatrecht möglicherweise zusätzliche Rechte:
        </p>
        <div className="award-laender-grid">
          <div className="award-land">
            <div className="award-land-code">CH</div>
            <div>
              <div className="award-land-name">Schweiz · nDSG</div>
              <div className="award-land-text">EDÖB · <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer">edoeb.admin.ch</a></div>
            </div>
          </div>
          <div className="award-land">
            <div className="award-land-code">UK</div>
            <div>
              <div className="award-land-name">UK · UK GDPR + DPA 2018</div>
              <div className="award-land-text">ICO · <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a></div>
            </div>
          </div>
          <div className="award-land">
            <div className="award-land-code">CA</div>
            <div>
              <div className="award-land-name">Kanada · PIPEDA</div>
              <div className="award-land-text">OPC · <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer">priv.gc.ca</a></div>
            </div>
          </div>
          <div className="award-land">
            <div className="award-land-code">BR</div>
            <div>
              <div className="award-land-name">Brasilien · LGPD</div>
              <div className="award-land-text">ANPD · <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer">gov.br/anpd</a></div>
            </div>
          </div>
          <div className="award-land">
            <div className="award-land-code">US</div>
            <div>
              <div className="award-land-name">USA</div>
              <div className="award-land-text">FGL verkauft keine personenbezogenen Daten. Mindestalter 16 übererfüllt COPPA.</div>
            </div>
          </div>
          <div className="award-land">
            <div className="award-land-code">..</div>
            <div>
              <div className="award-land-name">Weitere Länder</div>
              <div className="award-land-text">Japan APPI · Südkorea PIPA · Südafrika POPIA · Indien DPDP · Australien Privacy Act</div>
            </div>
          </div>
        </div>
        <p>
          Da FGL die DSGVO als höchsten Datenschutzstandard weltweit umsetzt, sind die
          Schutzniveaus der genannten Länder regelmäßig erfüllt oder übererfüllt. Bei
          Konflikten zwischen lokalem Recht und DSGVO gilt das jeweils{' '}
          <strong>höhere Schutzniveau zugunsten des Nutzers</strong>.
        </p>
      </>
    ),
  },
  {
    num: '§ 9', numDisplay: '09', title: 'Cookies und ähnliche Technologien', titleI18nKey: 'legal.datenschutzTitles.t09', summaryI18nKey: 'legal.datenschutzSummaries.t09',
    body: (
      <>
        <p>Die Plattform verwendet folgende Kategorien:</p>
        <ul>
          <li><strong>Technisch notwendige Cookies und Local-Storage-Einträge</strong>{' '}
            (keine Einwilligung erforderlich nach § 25 Abs. 2 TTDSG):
            <ul style={{ marginTop: '0.4em' }}>
              <li><code>NEXT_LOCALE</code> — gewählte Sprache, Speicherdauer 1 Jahr
                (HTTP-Cookie, funktional).</li>
              <li><code>fgl-theme</code> — gewähltes Theme, dauerhaft im
                Browser-localStorage (funktional).</li>
              <li><code>fgl-glider-on</code> — Schalter für die Hintergrund-Animation,
                dauerhaft im Browser-localStorage (funktional).</li>
              <li>Session-Cookie der Authentifizierung (NextAuth, HttpOnly, SameSite=Lax).</li>
              <li>Akzeptanz-Status des Cookie-Banners (localStorage).</li>
              <li>Cloudflare-Turnstile-Cookies — werden nur während des
                CAPTCHA-Vorgangs auf Registrierungs- und Bewerbungsformularen gesetzt
                (technisch erforderlich für die Bot-Abwehr; siehe § 5).</li>
            </ul>
          </li>
          <li><strong>Analyse</strong>: Soweit eine interne Reichweitenmessung
            stattfindet (z.&nbsp;B. Umami), werden ausschließlich anonymisierte
            Browser-Fingerprint-Hashes verwendet — <strong>kein Tracking-Cookie</strong>,
            keine Wiedererkennung über Sitzungen hinweg, keine externen Analyse-Cookies.
            Prometheus-Metriken sind ebenfalls anonymisiert.</li>
          <li><strong>Werbe-Cookies</strong>: Die Plattform setzt <strong>keine</strong>{' '}
            Werbe-Cookies und bindet keine Werbenetzwerke ein. Die Finanzierung erfolgt
            ausschließlich über freiwillige Digitale Trinkgelder und Förderbeiträge
            der Pilotinnen und Piloten.</li>
        </ul>
      </>
    ),
  },
  {
    num: '§ 10', numDisplay: '10', title: 'Drittland-Übermittlung', titleI18nKey: 'legal.datenschutzTitles.t10', summaryI18nKey: 'legal.datenschutzSummaries.t10',
    body: (
      <>
        <p>
          Grundsätzlich werden Daten ausschließlich innerhalb der EU verarbeitet. Eine
          Übermittlung in Drittländer erfolgt nur bei:
        </p>
        <ul>
          <li><strong>Cloudflare Turnstile</strong> (USA): Standardvertragsklauseln (SCC)
            liegen vor. Nur Bot-Abwehr-Token, keine Profilbildung.</li>
          <li><strong>PayPal</strong> (USA/Luxemburg): PayPal ist ein eigenverantwortlicher
            Zahlungsdienstleister. Datenübermittlung erfolgt nur bei aktivem
            Digitalen Trinkgeld bzw. Förderbeitrag.</li>
        </ul>
      </>
    ),
  },
  {
    num: '§ 10a', numDisplay: '10a', title: 'Community-Chat', titleI18nKey: 'legal.datenschutzTitles.t10a', summaryI18nKey: 'legal.datenschutzSummaries.t10a',
    body: (
      <>
        <p>
          Die Plattform stellt einen internen Community-Chat bereit. Verarbeitet werden:
        </p>
        <ul>
          <li><strong>Nachrichteninhalt</strong> (Text, optional Bilder): dauerhaft gespeichert,
            bis der Verfasser die Nachricht selbst löscht oder ein Administrator sie
            entfernt.</li>
          <li><strong>Bild-Originale</strong>: werden <strong>nach 8 Tagen</strong> automatisch
            aus dem Objektspeicher gelöscht. Komprimierte Vorschau (max. 400 px) bleibt
            dauerhaft erhalten.</li>
          <li><strong>Metadaten</strong>: Absender-ID, Kanal, Zeitstempel, Reply-Bezug,
            Weiterleitungs-Herkunft.</li>
          <li><strong>Lesestatus</strong>: pro Pilot und Kanal Zeitpunkt der letzten Einsicht.</li>
        </ul>
        <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Kommunikationsfunktion)
          und Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Pilot-Austausch).</p>
        <p><strong>Empfänger:</strong> Inhalte sind für alle eingeloggten Piloten im selben
          Kanal sichtbar. Öffentlich lesbare Kanäle sind zusätzlich für nicht eingeloggte
          Besucher einsehbar (Standard: Hauptkanäle öffentlich lesbar).</p>
        <p><strong>Ihre Rechte:</strong> Soft-Delete eigener Nachrichten jederzeit; bei
          Konto-Löschung werden Chat-Nachrichten anonymisiert oder vom Pilotenbezug gelöst.</p>
        <p><strong>Moderation:</strong> Administratoren können regelverletzende Nachrichten
          entfernen. Moderationsentscheidungen werden im Audit-Log dokumentiert.</p>
        <p><strong>Private Weiterführung:</strong> Über das Kontextmenü kann ein privater
          1:1-Chat mit dem Absender gestartet werden („Privat weiterführen").</p>
      </>
    ),
  },
  {
    num: '§ 10b', numDisplay: '10b', title: 'KI-Assistent und Wissensdatenbank (FREEGLIDY)', titleI18nKey: 'legal.datenschutzTitles.t10b', summaryI18nKey: 'legal.datenschutzSummaries.t10b',
    body: (
      <>
        <p>
          Die Plattform bietet einen KI-gestützten Chat-Assistenten, der vollständig auf
          eigener Infrastruktur in Deutschland betrieben wird. <strong>Es findet keine
          Übermittlung an OpenAI, Google, Anthropic oder sonstige externe KI-Dienste
          statt.</strong> Bei der Nutzung werden verarbeitet:
        </p>
        <ul>
          <li><strong>Fragen und Antworten</strong>: in einer Session-Struktur in der
            internen Plattform-Datenbank gespeichert.</li>
          <li><strong>Embedding-Vektoren</strong>: numerische Repräsentationen in einer
            selbst gehosteten internen Wissensdatenbank (<strong>kein externer Dienst</strong>),
            für Retrieval-Augmented Generation. Enthalten <strong>keine direkt lesbaren
            Textinhalte</strong>.</li>
          <li><strong>Metadaten</strong>: Session-ID, Zeitstempel, Token-Verbrauch.</li>
        </ul>
        <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (vereinbarte
          KI-Funktionen) und Art. 6 Abs. 1 lit. f DSGVO (Verbesserung Antwortqualität).</p>
        <p><strong>On-Premise KI — Ihre Daten bleiben bei uns:</strong> Alle KI-Komponenten
          laufen auf eigener Infrastruktur. Datenfluss: Browser → FGL-API (Hetzner) →
          verschlüsselter Tailscale-Tunnel (siehe § 5) → eigener KI-Hochleistungsserver
          in Deutschland → Antwort zurück. <strong>Keine US-Cloud, keine Anbindung an
          OpenAI, Anthropic, Google Gemini oder vergleichbare externe KI-Dienste.</strong>{' '}
          Eingegebene Prompts werden nicht in Klartext-Logs geschrieben (außer im Rahmen
          freiwilliger Bug-Reports).</p>
        <ul>
          <li>KI-Sprachmodelle laufen auf einem eigenen KI-Hochleistungsserver in
            Deutschland (Primary) sowie auf Hetzner Cloud (Fallback). Eingesetzt werden
            ausschließlich Open-Source-Modelle mit kommerziell zulässigen Lizenzen, die
            lokal ausgeführt werden — ohne Daten-Übermittlung an die Modell-Hersteller.</li>
          <li>Die Wissens-Vektoren liegen in einer selbst gehosteten internen
            Wissensdatenbank in eigenem Docker-Container.</li>
        </ul>
        <p><strong>Vision-Funktion (Bildbeschreibung):</strong> Beim Hochladen eines
          Bildes in den FREEGLIDY-Chat (z.&nbsp;B. Foto eines Modells, Cockpit-Foto)
          wird das Bild über den Tailscale-Tunnel an den eigenen KI-Hochleistungsserver
          in Deutschland übertragen. Dort beschreibt ein lokal ausgeführtes Vision-Modell
          das Bild als reinen Text. Das Bild wird nach erfolgter Beschreibung verworfen —
          gespeichert wird nur die textuelle Beschreibung im RAG-Wissensschatz.</p>
        <p><strong>Vorteile dieses Ansatzes:</strong></p>
        <ul>
          <li>Keine Daten verlassen die EU oder den Rechtsbereich des Verantwortlichen</li>
          <li>Keine Drittanbieter erhalten Einblick in Ihre Fragen oder Flugdaten</li>
          <li>Kein Training fremder KI-Modelle mit Ihren Daten</li>
          <li>Volle Kontrolle über Löschung — wenn Sie löschen, ist es physisch weg</li>
          <li>DSGVO-Konformität ohne Auftragsverarbeitungsvertrag mit US-Unternehmen</li>
        </ul>
        <p><strong>Strikte Personalisierung des KI-Memorys:</strong> Ihr Chat-Verlauf bleibt
          strikt Ihnen zugeordnet. Beim Aufbau des RAG-Kontextes sucht die Plattform{' '}
          <strong>ausschließlich</strong> in Vektoren mit Ihrer eigenen <code>userId</code>{' '}
          (technisch durchgesetzte Filterung in der internen Wissensdatenbank).</p>
        <p><strong>Steuerung Ihres KI-Gedächtnisses:</strong> In den{' '}
          <Link href="/app/settings/ai-privacy">FREEGLIDY-Privatsphäre-Einstellungen</Link>{' '}
          können Sie das Chat-Memory ein-/ausschalten, einzelne Sessions inkl. Vektoren
          unwiderruflich löschen oder sämtliche FREEGLIDY-Daten in einem Schritt entfernen.</p>
        <p><strong>Allgemeiner Wissensschatz:</strong> Neben dem privaten Memory führt
          FREEGLIDY einen vom Verantwortlichen gepflegten allgemeinen Wissensschatz
          (Plattform-Doku, FGL-Regelwerk-PDF, Hersteller-Handbücher, mit Beschreibungen
          versehene Bilder via lokalem Vision-Modell).</p>
        <p><strong>Wissensbeiträge der Community:</strong> Wenn Sie eine FREEGLIDY-Antwort
          als hilfreich markieren, können Sie sie als Beitrag zum allgemeinen Wissensschatz
          einreichen. Der Verantwortliche prüft, redigiert ggf. und gibt frei. Sie können
          einen genehmigten Beitrag jederzeit zurückziehen.</p>
        <p><strong>Löschung:</strong> Sessions und Embedding-Vektoren werden bei Konto-Löschung
          aus der internen Plattform-Datenbank und der internen Wissensdatenbank entfernt.</p>
      </>
    ),
  },
  {
    num: '§ 11', numDisplay: '11', title: 'Sicherheit (Art. 32 DSGVO)', titleI18nKey: 'legal.datenschutzTitles.t11', summaryI18nKey: 'legal.datenschutzSummaries.t11',
    body: (
      <>
        <p>
          Der Verantwortliche trifft angemessene technische und organisatorische Maßnahmen
          (TOM) zum Schutz der Daten:
        </p>
        <ul>
          <li>TLS-Verschlüsselung aller Verbindungen (Let's Encrypt, HSTS aktiviert)</li>
          <li>Passwort-Hashing mit bcrypt (Faktor 12)</li>
          <li>Zwei-Faktor-Authentifizierung (TOTP, AES-256-GCM verschlüsselt)</li>
          <li>IGC-Dateien in privatem S3-Bucket, Download mit signierten Tokens + Wasserzeichen</li>
          <li>Rate-Limiting (100 req/min pro IP) und Middleware-Security-Headers</li>
          <li>Verschlüsselte Backups (Restic AES-256) auf Hetzner Storage Box</li>
          <li>Getrennte Admin- und User-Rollen, Audit-Log für Admin-Aktionen</li>
          <li>SMTP-Passwörter und API-Keys verschlüsselt in der Datenbank, nicht im Klartext</li>
        </ul>
      </>
    ),
  },
  {
    num: '§ 12', numDisplay: '12', title: 'Datenpannen (Art. 33/34 DSGVO)', titleI18nKey: 'legal.datenschutzTitles.t12', summaryI18nKey: 'legal.datenschutzSummaries.t12',
    body: (
      <p>
        Im Falle einer Datenschutzverletzung mit Risiko für betroffene Personen wird der
        Verantwortliche die zuständige Aufsichtsbehörde innerhalb von <strong>72 Stunden</strong>{' '}
        informieren und — bei hohem Risiko — auch die Betroffenen unverzüglich benachrichtigen.
      </p>
    ),
  },
  {
    num: '§ 13', numDisplay: '13', title: 'Minderjährige', titleI18nKey: 'legal.datenschutzTitles.t13', summaryI18nKey: 'legal.datenschutzSummaries.t13',
    body: (
      <p>
        Die Plattform richtet sich an Personen ab Vollendung des 16. Lebensjahres (Art. 8
        DSGVO, § 22a BDSG). Minderjährige unter 16 Jahren dürfen die registrierungspflichtigen
        Funktionen nur mit nachweislicher Zustimmung der Erziehungsberechtigten nutzen.
        Details regeln die <Link href="/nutzungsbedingungen">Nutzungsbedingungen</Link>.
      </p>
    ),
  },
  {
    num: '§ 14', numDisplay: '14', title: 'Keine Fremddaten, kein Scraping', titleI18nKey: 'legal.datenschutzTitles.t14', summaryI18nKey: 'legal.datenschutzSummaries.t14',
    body: (
      <p>
        Die Plattform ist eine Analyse- und Wertungs-Plattform. Es werden{' '}
        <strong>ausschließlich Daten verarbeitet, die der Nutzer selbst aktiv hochlädt</strong>.
        Es findet kein Import aus externen Datenquellen, kein Scraping, kein automatisierter
        Abruf fremder Flugdaten statt. Dieser Grundsatz ist fester Bestandteil der technischen
        Architektur (Privacy by Design gemäß Art. 25 DSGVO).
      </p>
    ),
  },
  {
    num: '§ 14a', numDisplay: '14a', title: 'Drittanbieter-Datenflüsse beim Aufruf von Karten und Inhalten', titleI18nKey: 'legal.datenschutzTitles.t14a', summaryI18nKey: 'legal.datenschutzSummaries.t14a',
    body: (
      <>
        <p>
          Beim Aufruf bestimmter Seiten der Plattform werden vom Browser des Nutzers
          aus direkte Verbindungen zu externen Servern aufgebaut. Diese Datenflüsse
          gehen <strong>nicht vom FGL-Server</strong> aus, sondern entstehen technisch
          notwendigerweise durch die Einbindung externer Karten- und Bilddienste. Dabei
          werden in der Regel die IP-Adresse, der User-Agent und die jeweils angefragte
          Ressource übermittelt. Eine Profilbildung durch FGL findet nicht statt; die
          Verarbeitung beim Drittanbieter unterliegt dessen jeweiliger
          Datenschutzerklärung.
        </p>
        <ul>
          <li><strong>OpenStreetMap Foundation</strong> (<code>tile.openstreetmap.org</code>) —
            Standard-Kartenkacheln auf Seiten wie <code>/flying-fields</code>. Lizenz
            ODbL 1.0 (kommerzielle Nutzung mit Quellenangabe erlaubt).</li>
          <li><strong>OpenTopoMap</strong> (<code>opentopomap.org</code>) — Topographische
            Karte. Lizenz CC BY-SA 3.0.</li>
          <li><strong>EOX IT Services GmbH</strong> (<code>tiles.maps.eox.at</code>) —
            Sentinel-2-Satellitenkachel-Layer. Lizenz CC BY 4.0.</li>
          <li><strong>flagcdn.com</strong> — Anzeige von Länderflaggen im Sprach-Umschalter.
            Frei kommerziell nutzbar.</li>
          <li><strong>openAIP</strong> — Luftraum-Tile-Overlay für die 2D- und
            3D-Luftraumkarte. Free-Tier mit kommerzieller Nutzung; ggf. Wechsel auf
            Premium-Tier bei höherem Volumen.</li>
        </ul>
        <p>
          Bei Wetterdaten-Aufrufen (DWD/Bright Sky, NOAA, JMA, MET Norway) erfolgt der
          Abruf stets <strong>serverseitig</strong> durch die FGL-Plattform — dabei wird
          die IP-Adresse des FGL-Servers an den Wetterdienst übermittelt, nicht die
          IP-Adresse des Nutzers (siehe § 4b Abs. 3 und § 5).
        </p>
        <p style={{ marginTop: '1rem', padding: '0.85rem 1rem', borderLeft: '3px solid var(--award-gold)', background: 'color-mix(in oklch, var(--award-gold) 8%, transparent)' }}>
          <strong style={{ color: 'var(--award-gold)' }}>Hinweis:</strong>{' '}
          Tile-Aufrufe an OpenStreetMap, OpenTopoMap, EOX und flagcdn führen zu einem
          direkten Browser-zu-Server-Datenfluss, der von jeder webbasierten Karte
          ausgelöst wird. Im <strong>Cookie-Banner</strong> kann unter „Details
          anzeigen" die Kategorie „Externe Karten-Tiles" einzeln abgelehnt werden.
          Wer diese Einwilligung verweigert, vermeidet das Laden externer
          Hintergrundkarten in den Komponenten, die diese Einwilligung bereits
          prüfen; die Umsetzung wird über die nächsten Versionen auf alle
          Karten-Komponenten ausgerollt. Wer auf Nummer sicher gehen will, ruft
          die entsprechende Karten-Ansicht nicht auf.
        </p>
      </>
    ),
  },
  {
    num: '§ 14b', numDisplay: '14b', title: 'Schriftarten — lokale Auslieferung', titleI18nKey: 'legal.datenschutzTitles.t14b', summaryI18nKey: 'legal.datenschutzSummaries.t14b',
    body: (
      <>
        <p>
          Sämtliche auf der Plattform verwendeten Schriftarten werden seit Mai 2026{' '}
          <strong>ausschließlich lokal vom FGL-Server ausgeliefert</strong>{' '}
          (Pfad <code>/fonts/...</code>). Es findet <strong>kein Aufruf an
          Drittanbieter-Schrift-CDNs</strong> (etwa Google Fonts oder Fontshare) statt.
          Damit entsteht durch das Laden von Schriften{' '}
          <strong>kein datenschutzrelevanter Datenfluss</strong> zu Dritten — insbesondere
          keine Übermittlung von IP-Adresse oder User-Agent an externe Schriften-Anbieter.
        </p>
        <p>
          Frühere Versionen der Plattform (vor April&nbsp;2026) banden Schriften
          teilweise direkt von Drittanbieter-CDNs ein. Diese Einbindungen wurden mit dem
          Schriften-Refactoring im Mai&nbsp;2026 vollständig entfernt.
        </p>
      </>
    ),
  },
  {
    num: '§ 14c', numDisplay: '14c', title: 'Verwendung von Pilot-Fotos in News, Newsletter und FB-Gruppe', titleI18nKey: 'legal.datenschutzTitles.t14c', summaryI18nKey: 'legal.datenschutzSummaries.t14c',
    body: (
      <>
        <p>
          Mit dem Hochladen eines Fotos auf die Plattform (Galerie, Profilbild,
          Flug-Bilder) räumt die Pilotin / der Pilot der FREE GLIDE LEAGUE /
          Modellpilot.EU ein <strong>einfaches, nicht-ausschließliches Nutzungsrecht</strong>{' '}
          ein, das Foto auf der Plattform anzuzeigen sowie zu redaktionellen Zwecken in
        </p>
        <ul>
          <li>News-Beiträgen auf <a href="https://free-glide-league.com">free-glide-league.com</a>,</li>
          <li>dem FGL-Newsletter und</li>
          <li>der offiziellen Facebook-Gruppe der FREE GLIDE LEAGUE</li>
        </ul>
        <p>
          zu veröffentlichen. Schöne Modellflugzeug-Fotos können zusätzlich als
          Standard-Modellfoto in der öffentlichen Modell-Datenbank genutzt werden.
          Bei jeder Veröffentlichung wird der Pilot mit Anzeigename und Profil-Link
          als Bildquelle genannt.
        </p>
        <p>
          Das <strong>Urheberrecht verbleibt vollständig</strong> bei der Pilotin /
          dem Piloten. Eine kommerzielle Drittnutzung außerhalb dieser Zwecke findet
          nicht statt.
        </p>
        <p>
          Die Pilotin / der Pilot versichert beim Hochladen, <strong>Urheber</strong> des
          Fotos zu sein oder über sämtliche erforderlichen Rechte zu verfügen. Sind{' '}
          <strong>weitere Personen</strong> auf dem Foto erkennbar, garantiert sie/er,
          deren Einwilligung gemäß § 22 KUG eingeholt zu haben, und kann diese{' '}
          <strong>auf Nachfrage Dritter eigenverantwortlich nachweisen</strong>.
        </p>
        <p>
          Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung mit Registrierung)
          sowie § 31 UrhG (einfache Lizenz). Die Einwilligung ist freiwillig und kann
          jederzeit mit Wirkung für die Zukunft per E-Mail an{' '}
          <a href="mailto:info@free-glide-league.com">info@free-glide-league.com</a> widerrufen werden.
          Bereits erfolgte Veröffentlichungen werden nach Widerruf entfernt, soweit
          dies technisch zumutbar ist.
        </p>
      </>
    ),
  },
  {
    num: '§ 14d', numDisplay: '14d', title: 'Klarnamen-Pflicht für Teilnahme an der FGL-Wertung', titleI18nKey: 'legal.datenschutzTitles.t14d', summaryI18nKey: 'legal.datenschutzSummaries.t14d',
    body: (
      <>
        <p>
          Die FREE GLIDE LEAGUE ist eine Analyse- und Wertungs-Plattform für RC-Modellsegelflug.
          Die Teilnahme an der offiziellen FGL-Wertung setzt zwingend die Angabe eines
          vollständigen <strong>Vor- und Nachnamens</strong> (Klarname) bei der
          Registrierung voraus. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO
          (Vertragserfüllung — Teilnahme am Vergleichsfliegen erfordert eindeutige
          Identifizierbarkeit der Wertungsteilnehmer) in Verbindung mit § 6 der
          Wettbewerbsordnung.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Spitzname / Alias — ausschließlich interner Gebrauch:</strong> Ein
          frei wählbarer Spitzname dient ausschließlich der Bekanntgabe und der
          persönlichen Ansprache zwischen eingeloggten Piloten im internen
          Mitgliederbereich. Im <strong>Piloten-Profil, in der offiziellen Wertung
          und in allen sonstigen Erwähnungen</strong> (Wettbewerbs-Veröffentlichungen,
          News-Beiträge, Newsletter, externe Bekanntgaben) wird ausschließlich der
          Klarname verwendet.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Konsequenzen bei fehlendem oder unzutreffendem Klarnamen:</strong>
        </p>
        <ul style={{ marginTop: '0.5em', paddingLeft: '1.2em' }}>
          <li>
            <strong>Kein Klarname hinterlegt</strong> — die Plattform kann zwar
            vollumfänglich genutzt werden (Uploads, Analyse, Coaching, Thermik-Atlas,
            Polaren), eine Teilnahme an der offiziellen FGL-Wertung ist jedoch
            <strong> nicht möglich</strong>.
          </li>
          <li>
            <strong>Falsche Deklaration</strong> (z. B. erfundener Name, Pseudonym
            als Klarname): nachträgliche <strong>Disqualifikation</strong> aller
            betroffenen Wertungsergebnisse und <strong>Ausschluss von der weiteren
            Teilnahme</strong> an der FGL-Wertung. Die Plattform-Nutzung selbst
            (ohne Wertung) bleibt davon unberührt.
          </li>
        </ul>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Änderung des Klarnamens:</strong> Nach erfolgter Registrierung kann
          der eingetragene Vor- und Nachname vom Pilot selbst nicht mehr geändert
          werden, um Manipulationen am Wertungs-Verlauf auszuschließen. Bei
          berechtigten Anlässen (Heirat, Tippfehler, Namensänderung) genügt eine kurze
          E-Mail an <a href="mailto:info@free-glide-league.com">info@free-glide-league.com</a>{' '}
          mit Begründung — die Änderung erfolgt durch die Administration.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Anzeige der Klarnamen unmittelbarer Wettbewerbs-Nachbarn
          (Verfolger-Modus, ab 19.06.2026):</strong> Im eingeloggten
          Mitgliederbereich kann jeder Pilot in seiner Sicht „Meine Platzierungen" zwischen
          den Modi <strong>„Nur ich"</strong> und <strong>„Inkl. Verfolger"</strong> wechseln.
          Im Verfolger-Modus werden pro gerankter Wertungs-Kategorie die Klarnamen sowie
          (sofern verfügbar) die Avatar-Bilder der unmittelbar vor- und nachgelagerten
          Wertungsteilnehmenden sowie des aktuellen Spitzenreiters angezeigt, jeweils
          gemeinsam mit dem rechnerischen Wertungs-Abstand in Kilometern. Zusätzlich kann
          ein KI-generierter Kurzbericht (Sportreporter-Stil) ergänzt werden, der diese
          Abstände in einen motivierenden Kontext einordnet.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Rechtsgrundlage des Verfolger-Modus:</strong> Art. 6 Abs. 1 lit. b DSGVO
          (Vertragserfüllung — der Wettbewerbsvergleich ist intrinsischer Bestandteil der
          Teilnahme an der FGL-Wertung) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes
          Interesse an Wettkampf-Transparenz; das schutzwürdige Interesse des Einzelnen
          überwiegt nicht, weil die Anzeige nur Klarname und Wertungs-Strecke umfasst,
          ausschließlich für eingeloggte Vertragspartner sichtbar ist und die Klarnamen-
          Pflicht für die Wertungs-Teilnahme bereits durch § 14d Abs. 1 bekannt war).
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Sichtbarkeit:</strong> Der Verfolger-Modus ist <strong>ausschließlich für
          eingeloggte Pilotinnen und Piloten</strong> abrufbar. Auf öffentlichen Seiten ohne
          Login (Wertungslisten, Pilotenprofile, Suchmaschinen-Vorschauen) wird nie eine
          Verfolger-Liste mit Klarnamen ausgespielt. Die zugehörigen API-Endpunkte
          (<code>/api/rankings/my-rankings</code> und <code>/api/rankings/my-rankings/coaching</code>)
          verweigern anonyme Anfragen mit HTTP 401.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Widerspruch:</strong> Wer der Verarbeitung im Verfolger-Modus gemäß Art. 21 DSGVO
          widersprechen möchte, kann eine kurze E-Mail an{' '}
          <a href="mailto:info@free-glide-league.com">info@free-glide-league.com</a> senden.
          Beim Eingang prüfen wir den Einzelfall; ein erfolgreicher Widerspruch führt dazu,
          dass der Klarname dieses Piloten gegenüber anderen <strong>nicht mehr</strong> als
          Verfolger / Vorgänger / Spitze angezeigt wird (die Position selbst bleibt aus
          Gründen der Wettbewerbs-Integrität sichtbar). Dies kann zur Folge haben, dass
          die Teilnahme an der FGL-Wertung nicht mehr möglich ist, da die Klarnamen-Pflicht
          aus § 14d Abs. 1 hierdurch nicht mehr erfüllbar ist.
        </p>
        <p style={{ marginTop: '0.75em', fontSize: '0.92em', opacity: 0.85 }}>
          Hinweis zur Datenminimierung (Art. 5 Abs. 1 lit. c DSGVO): Eine Indexierung
          der Klarnamen durch externe Suchmaschinen erfolgt nicht; öffentliche
          Listen-Seiten der Wertung werden mit <code>noindex</code> ausgeliefert.
        </p>
      </>
    ),
  },
  {
    num: '§ 14f', numDisplay: '14f', title: 'Online-Status im eingeloggten Mitgliederbereich', titleI18nKey: 'legal.datenschutzTitles.t14f', summaryI18nKey: 'legal.datenschutzSummaries.t14f',
    body: (
      <>
        <p>
          Innerhalb des <strong>eingeloggten Mitgliederbereichs</strong> kann an
          Profilbildern ein kleiner farbiger Punkt erscheinen, der einen eingeschränkten
          Online-Status zeigt:
        </p>
        <ul style={{ marginTop: '0.5em', paddingLeft: '1.2em' }}>
          <li><strong>Grün, pulsierend</strong> — Pilot ist aktuell online (Heartbeat in den letzten ca. 3 Minuten).</li>
          <li><strong>Blau, statisch</strong> — Pilot war innerhalb der letzten 24 Stunden online, ist aktuell aber nicht aktiv.</li>
          <li><strong>Kein Punkt</strong> — Pilot war seit mehr als 24 Stunden nicht online oder Status nicht verfügbar.</li>
        </ul>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Datenminimierung (Art. 5 Abs. 1 lit. c DSGVO):</strong> Es werden
          keine exakten Login-Zeitstempel öffentlich angezeigt. Sichtbar ist ausschließlich
          die abgeleitete Status-Stufe (online / heute / nichts), nicht die genaue Uhrzeit
          des letzten Aufrufs.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Sichtbarkeit:</strong> Der Online-Status ist <strong>ausschließlich für
          eingeloggte Pilotinnen und Piloten</strong> abrufbar. Auf öffentlichen Seiten
          (Wertungslisten ohne Login, Pilotenprofil ohne Login, Suchmaschinen-Vorschau)
          erscheint <strong>nie</strong> ein Online-Status-Punkt. Der zugehörige API-Endpunkt
          (<code>/api/presence</code>) verweigert anonyme Anfragen mit HTTP 401.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Zweck:</strong> Die Anzeige dient ausschließlich der Community-Funktion
          innerhalb des geschlossenen Mitgliederbereichs (z. B. zu erkennen, ob ein
          Empfänger einer Nachricht gerade erreichbar ist). Rechtsgrundlage: Art. 6 Abs. 1
          lit. b DSGVO (Vertragserfüllung — Community-Funktion der Plattform).
        </p>
        <p style={{ marginTop: '0.75em', fontSize: '0.92em', opacity: 0.85 }}>
          Technische Umsetzung: Beim Aufruf von Seiten im eingeloggten Bereich wird in
          unregelmäßigen Abständen ein Heartbeat-Aufruf (<code>POST /api/me/heartbeat</code>)
          ausgelöst, der den Zeitpunkt der letzten Aktivität speichert (Feld
          <code>users.last_seen_at</code>). Die Auswertung zu einer Status-Stufe erfolgt
          serverseitig; an Dritte werden weder Zeitstempel noch Heartbeat-Frequenz übermittelt.
        </p>
      </>
    ),
  },
  {
    num: '§ 14g', numDisplay: '14g', title: 'Veröffentlichung von Wertungsdaten und Datums-Suche', titleI18nKey: 'legal.datenschutzTitles.t14g', summaryI18nKey: 'legal.datenschutzSummaries.t14g',
    body: (
      <>
        <p>
          Wer in der FGL-Wertung mitfliegt, willigt explizit in die <strong>weltweit
          öffentliche Anzeige</strong> der folgenden Daten ein:
        </p>
        <ul style={{ marginTop: '0.5em', paddingLeft: '1.2em' }}>
          <li>Vor- und Nachname (Klarnamen-Pflicht, siehe § 14d)</li>
          <li>Flugdatum, Wertungsklasse, Strecke, Durchschnittsgeschwindigkeit, Flugdauer</li>
          <li>Landeskennzeichen (Flagge) und ggf. Vereinszuordnung</li>
          <li>Modellbezeichnung des Fluggeräts</li>
        </ul>
        <p style={{ marginTop: '0.75em' }}>
          Diese Daten sind über die <strong>öffentliche Wertungs-Seite</strong>
          (<code>/wertung</code>), den <strong>Tagesticker</strong>, das
          <strong> öffentliche Pilotenprofil</strong> (<code>/pilot/[slug]</code>) und
          die zugehörigen <strong>öffentlichen APIs</strong> abrufbar. Die Anzeige
          erfolgt <strong>nach Datum, Klasse und Pilot durchsuchbar</strong> — sowohl
          tagesaktuell als auch rückblickend über mehrere Jahre (Achievement-Block,
          Saison-Statistiken).
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (ausdrückliche
          Einwilligung). Wer keinen aktiven Consent für die jeweils aktuelle Fassung
          dieser Datenschutzerklärung erteilt hat, wird <strong>nicht</strong> in den
          öffentlichen Wertungs-Anzeigen geführt.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Widerruf jederzeit möglich (Art. 7 Abs. 3 DSGVO):</strong> in den
          Konto-Einstellungen unter „Datenschutz / Wertungs-Veröffentlichung" oder per
          E-Mail an <a href="mailto:info@free-glide-league.com">info@free-glide-league.com</a>.
          Bei Widerruf werden alle bisherigen Wertungseinträge aus den öffentlichen
          Anzeigen entfernt; bereits ausgelieferte aggregierte Statistiken bleiben
          unverändert. Die Teilnahme an der FGL-Wertung endet mit dem Widerruf.
        </p>
        <p style={{ marginTop: '0.75em', fontSize: '0.92em', opacity: 0.85 }}>
          Technisch wird der Consent in der Tabelle <code>consent_records</code>
          versioniert protokolliert (Art. 7 Abs. 1 DSGVO Nachweispflicht). Bei
          Versions-Wechsel dieser Datenschutzerklärung wird ein Re-Consent-Hinweis
          eingeblendet; bis zur erneuten Bestätigung wird die Anzeige in der
          öffentlichen Wertung pausiert.
        </p>
      </>
    ),
  },
  {
    num: '§ 14h', numDisplay: '14h', title: 'IGC-Datenexport, Konto-Löschung (Double-Opt-In) und Forensik-Backup', titleI18nKey: 'legal.datenschutzTitles.t14h', summaryI18nKey: 'legal.datenschutzSummaries.t14h',
    body: (
      <>
        <p>
          <strong>IGC-Datenexport (Art. 20 DSGVO — Recht auf Datenübertragbarkeit):</strong>{' '}
          Jeder Pilot kann seine hochgeladenen Originaldateien (IGC-Files) jederzeit in den
          Einstellungen einzeln oder als Bulk-ZIP anfordern. Der Download-Link wird an die
          Account-E-Mail-Adresse zugestellt und ist sieben Tage gültig; danach wird die
          erzeugte ZIP automatisch gelöscht. Pro 24 Stunden sind maximal drei Anforderungen
          möglich (Spam-Schutz). Hinweis: Die FREE GLIDE LEAGUE ist keine Archivplattform —
          eine eigene Sicherung der Originale (Cloud-Drive, externe Festplatte) bleibt
          Verantwortung des Piloten.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Konto-Löschung (Art. 17 DSGVO — Recht auf Löschung):</strong> Die Löschung
          erfolgt im <strong>Double-Opt-In</strong>:
        </p>
        <ol style={{ marginTop: '0.5em', paddingLeft: '1.2em' }}>
          <li>Der Pilot beantragt die Löschung in den Einstellungen und liest die Aufklärung über die Konsequenzen.</li>
          <li>Eine Bestätigungs-Mail mit Einmal-Link wird an die Account-Adresse gesendet (Link 24 Stunden gültig).</li>
          <li>Erst nach Klick auf den Link ist im Browser die Pflicht-Eingabe „LOESCHEN" freigeschaltet.</li>
          <li>Zusätzlich ist ein frischer 2FA-Code (TOTP oder E-Mail-Code) zwingend.</li>
          <li>Danach erfolgt die unverzügliche Löschung der personenbezogenen Daten.</li>
        </ol>
        <p style={{ marginTop: '0.75em' }}>
          Der Double-Opt-In schützt vor versehentlicher Löschung und vor Löschung durch eine
          kompromittierte Session. Rechtsgrundlage: Art. 12 Abs. 6 DSGVO (Authentifizierung
          der antragstellenden Person).
        </p>
        <p style={{ marginTop: '0.75em' }}>
          Bei der Löschung werden insbesondere entfernt: Flüge inkl. IGC-Originale, Tracks,
          Analysen, Wertungs-Beiträge, Thermik-Atlas-Beiträge (anonymisierte Aggregation),
          Fotos und Audio-Berichte, Cirra/Freeglidy-Chat-Historie (auch aus der KI-Wissens­basis),
          Modell-Sammlung, Badges, Likes und Follows. Kommentare des gelöschten Piloten werden
          zur Wahrung der Threadintegrität auf „[Gelöschter Benutzer]" anonymisiert.
        </p>
        <p style={{ marginTop: '0.75em' }}>
          <strong>Forensik-Backup mit Object-Lock (Art. 17 Abs. 3 lit. b/e DSGVO):</strong>{' '}
          Aus rechtlichen Gründen — insbesondere zum Nachweis bei begründetem
          Manipulations-Verdacht (Cheating-Kontrolle der FGL-Wertung) — wird beim Upload
          eine schreibgeschützte Kopie der IGC-Originale in einem separaten Vault-Bucket
          mit S3-Object-Lock-Compliance-Retention abgelegt. Diese Vault-Kopie ist
          ausschließlich für den Datenschutzbeauftragten und nicht über die Plattform-API
          zugreifbar; sie kann auch nicht durch Administratoren oder Server-Zugänge gelöscht
          werden. Die Retention läuft nach Ablauf der gesetzlichen Verjährungsfrist (zwei
          Jahre ab Upload) automatisch ab; danach wird die Datei vom Cloud-Anbieter
          unwiderruflich entfernt. Auf Antrag kann der Datenschutzbeauftragte unter
          Abwägung berechtigter Interessen eine vorzeitige Löschung prüfen.
        </p>
        <p style={{ marginTop: '0.75em', fontSize: '0.92em', opacity: 0.85 }}>
          Hinweis zur erneuten Registrierung: Nach erfolgter Konto-Löschung kann sich der
          Pilot mit derselben E-Mail-Adresse jederzeit neu registrieren. Es entsteht dann
          ein vollständig neuer Account ohne Zugriff auf alte Flüge oder Wertungs-Beiträge;
          ein erneuter Upload identischer IGC-Dateien ist möglich (Duplikat-Prüfung ist
          accountgebunden).
        </p>
      </>
    ),
  },
  {
    num: '§ 15', numDisplay: '15', title: 'Änderungen dieser Datenschutzerklärung', titleI18nKey: 'legal.datenschutzTitles.t15', summaryI18nKey: 'legal.datenschutzSummaries.t15',
    body: (
      <p>
        Diese Datenschutzerklärung kann angepasst werden, wenn sich rechtliche Anforderungen,
        technische Voraussetzungen oder die verwendeten Verarbeitungen ändern. Die jeweils
        aktuelle Fassung ist unter{' '}
        <a href="/datenschutz">free-glide-league.com/datenschutz</a> abrufbar. Wesentliche
        Änderungen werden registrierten Nutzern per E-Mail angekündigt.
      </p>
    ),
  },
]

export default function DatenschutzPage() {
  const { t } = useTranslation()
  return (
    <AwardLegalShell
      eyebrow={t('legal.datenschutz.eyebrow')}
      meta={t('legal.datenschutz.meta')}
      title={t('legal.datenschutz.title')}
      gradWord={t('legal.datenschutz.gradWord')}
      lead={t('legal.datenschutz.lead')}
      chips={[
        { label: t('legal.datenschutz.chip1') },
        { label: t('legal.datenschutz.chip2') },
        { label: t('legal.datenschutz.chip3') },
        { label: t('legal.datenschutz.chip4') },
      ]}
      tocEyebrow={t('legal.datenschutz.tocEyebrow')}
      tocTitle={t('legal.shared.tocTitle')}
      articles={articles}
      closingEyebrow={t('legal.datenschutz.closingEyebrow')}
      closingTitle={
        <>
          {t('legal.datenschutz.closingPrefix')}{' '}
          <Link href="/nutzungsbedingungen" className="award-link">{t('legal.datenschutz.closingNutzungsbedingungen')}</Link>{' '}
          {t('legal.datenschutz.closingAnd')}{' '}
          <Link href="/wettbewerbsordnung" className="award-link">{t('legal.datenschutz.closingWettbewerbsordnung')}</Link>.
        </>
      }
      primaryCta={{ href: '/app/settings/ai-privacy', label: t('legal.datenschutz.primaryCta') }}
      secondaryCta={{ href: '/', label: t('legal.datenschutz.secondaryCta') }}
    />
  )
}
